/**
 * Capturing the Collaborative Browser as this client sees it (ADR-0137). The
 * page renders on the Environment, so a user's screenshot or recording is taken
 * from the panel's own canvas and saved to this computer, where the user is.
 */

export function canvasPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("The browser view has nothing to capture yet."));
    }, "image/png");
  });
}

/** Records the canvas until `stop`; frames are only as frequent as the page changes. */
export function recordCanvas(canvas: HTMLCanvasElement): { readonly stop: () => Promise<Blob> } {
  const mimeType =
    ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"].find((type) =>
      MediaRecorder.isTypeSupported(type),
    ) ?? "video/webm";
  const recorder = new MediaRecorder(canvas.captureStream(30), { mimeType });
  const chunks: Blob[] = [];
  recorder.addEventListener("dataavailable", (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  });
  recorder.start(1_000);
  return {
    stop: () =>
      new Promise((resolve) => {
        recorder.addEventListener("stop", () => resolve(new Blob(chunks, { type: mimeType })), {
          once: true,
        });
        recorder.stop();
      }),
  };
}

export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  // The download has its own copy once the click is handled.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * A file name from the page's host and the time, safe on every filesystem. `fallback` names
 * a capture with no page, such as an idle tab.
 */
export function captureFileName(
  pageUrl: string,
  extension: string,
  now = new Date(),
  fallback = "page",
): string {
  let host = fallback;
  try {
    host = new URL(pageUrl).hostname.replace(/[^a-z0-9.-]+/gi, "-") || fallback;
  } catch {
    // An idle tab has no URL; the generic name is fine.
  }
  const stamp = now.toISOString().replace(/[:.]/g, "-").replace(/Z$/, "");
  return `${host}-${stamp}.${extension}`;
}
